/**
 * Shared Constants
 * Application-wide constants following best practices
 */

// Import ENV at the top to ensure it's initialized before use
import { ENV } from '@/config/environment';

// === API Endpoints ===
export const API_ENDPOINTS = {
  // Auth endpoints - Following FRONTEND_API_GUIDE
  AUTH: {
    LOGIN: '/auth/login',
    LOGOUT: '/auth/logout',
    VERIFY: '/auth/verify',
    REGISTER: '/auth/register',
    CHANGE_PASSWORD: '/auth/change-password',
    FORGOT_PASSWORD: '/auth/forgot-password',
    RESET_PASSWORD: '/auth/reset-password',
  },
  
  // User endpoints - Following FRONTEND_API_GUIDE
  USERS: {
    FETCH: '/users/fetch',
    UPDATE: '/users/update',
    SETTINGS: '/users/settings',
    ACTIVITY: '/users/activity',
    METADATA: '/users/metadata',
    ME: '/users/me', // Current user endpoint (used for updates)
  },
  
  // Blog endpoints - Following FRONTEND_API_GUIDE
  BLOGS: {
    FETCHALL: '/blogs/fetchall',
    FETCH: '/blogs/fetch',
    BY_SLUG: (slug: string) => `/blogs/slug/${slug}`,
    CREATE: '/blogs/create',
    UPDATE: '/blogs/update',
    DELETE: '/blogs/delete',
    PUBLISH: '/blogs/publish',
    CATEGORIES: '/blogs/categories',
    TAGS_SUGGESTIONS: '/blogs/tags/suggestions',
    AUTOSAVE: '/blogs/autosave',
    // Dynamic endpoints
    COMMENTS: (blogId: string) => `/blogs/${blogId}/comments`,
    LIKE: (blogId: string) => `/blogs/${blogId}/like`,
    LIKES_CHECK: (blogId: string) => `/blogs/${blogId}/likes/check`,
    COMMENT_UPDATE: (commentId: string) => `/blogs/comments/${commentId}`,
    COMMENT_DELETE: (commentId: string) => `/blogs/comments/${commentId}`,
    COMMENT_LIKE: (commentId: string) => `/blogs/comments/${commentId}/like`,
    SHARE: (blogId: string) => `/blogs/${blogId}/share`,
    STATS: (blogId: string) => `/blogs/${blogId}/stats`,
    MODERATE: (blogId: string) => `/blogs/admin/blogs/${blogId}/moderate`,
  },
  
  // File Upload endpoints - Following FRONTEND_API_GUIDE
  UPLOADS: {
    IMAGES: '/uploads/images',
    DELETE_IMAGE: (filename: string) => `/uploads/images/${filename}`,
    HEALTH: '/uploads/health',
  },
  
  // Analytics endpoints - Following FRONTEND_API_GUIDE (Admin only)
  ANALYTICS: {
    USERS_COUNT: '/analytics/users/count',
    SESSIONS_ACTIVE: '/analytics/sessions/active',
    USERS_GROWTH: '/analytics/users/growth',
    REVENUE: '/analytics/revenue',
    TRACK_PAGEVIEW: '/analytics/track/pageview',
    VISITORS: '/analytics/visitors',
    TRAFFIC_SOURCES: '/analytics/traffic/sources',
  },
  
  // Admin endpoints - Following FRONTEND_API_GUIDE
  ADMIN: {
    USERS: '/admin/users',
    BLOGS: '/admin/blogs',
    SYSTEM_INFO: '/admin/system/info',
    STATS: '/admin/stats',
    LOGS: '/admin/logs',
    MAINTENANCE: '/admin/maintenance',
  },
} as const;

// === HTTP Status Codes ===
export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  VALIDATION_ERROR: 422,
  INTERNAL_SERVER_ERROR: 500,
  SERVICE_UNAVAILABLE: 503,
} as const;

// === Error Messages ===
export const ERROR_MESSAGES = {
  // Generic errors
  GENERIC: 'Something went wrong. Please try again.',
  NETWORK: 'Network error. Please check your connection.',
  TIMEOUT: 'Request timed out. Please try again.',
  UNAUTHORIZED: 'You are not authorized to perform this action.',
  FORBIDDEN: 'Access forbidden.',
  NOT_FOUND: 'Resource not found.',
  
  // Auth errors
  INVALID_CREDENTIALS: 'Invalid email or password.',
  EMAIL_ALREADY_EXISTS: 'Email already exists.',
  USERNAME_ALREADY_EXISTS: 'Username already exists.',
  WEAK_PASSWORD: 'Password is too weak.',
  PASSWORDS_DO_NOT_MATCH: 'Passwords do not match.',
  TOKEN_EXPIRED: 'Session expired. Please login again.',
  EMAIL_NOT_VERIFIED: 'Please verify your email address.',
  
  // Validation errors
  REQUIRED_FIELD: 'This field is required.',
  INVALID_EMAIL: 'Please enter a valid email address.',
  INVALID_URL: 'Please enter a valid URL.',
  INVALID_DATE: 'Please enter a valid date.',
  MIN_LENGTH: (min: number) => `Minimum ${min} characters required.`,
  MAX_LENGTH: (max: number) => `Maximum ${max} characters allowed.`,
  
  // Blog errors
  BLOG_NOT_FOUND: 'Blog post not found.',
  BLOG_ALREADY_PUBLISHED: 'Blog post is already published.',
  BLOG_CANNOT_EDIT_PUBLISHED: 'Cannot edit published blog post.',
  
  // Project errors
  PROJECT_NOT_FOUND: 'Project not found.',
  
  // File upload errors
  FILE_TOO_LARGE: 'File is too large.',
  INVALID_FILE_TYPE: 'Invalid file type.',
  FILE_UPLOAD_FAILED: 'File upload failed.',
} as const;

// === Success Messages ===
export const SUCCESS_MESSAGES = {
  // Auth
  LOGIN_SUCCESS: 'Welcome back!',
  LOGOUT_SUCCESS: 'Logged out successfully.',
  REGISTER_SUCCESS: 'Account created successfully.',
  PASSWORD_CHANGED: 'Password changed successfully.',
  EMAIL_VERIFIED: 'Email verified successfully.',
  
  // Blog
  BLOG_CREATED: 'Blog post created successfully.',
  BLOG_UPDATED: 'Blog post updated successfully.',
  BLOG_DELETED: 'Blog post deleted successfully.',
  BLOG_PUBLISHED: 'Blog post published successfully.',
  BLOG_UNPUBLISHED: 'Blog post unpublished successfully.',
  
  // Project
  PROJECT_CREATED: 'Project created successfully.',
  PROJECT_UPDATED: 'Project updated successfully.',
  PROJECT_DELETED: 'Project deleted successfully.',
  
  // Profile
  PROFILE_UPDATED: 'Profile updated successfully.',
  
  // Generic
  SAVE_SUCCESS: 'Changes saved successfully.',
  DELETE_SUCCESS: 'Deleted successfully.',
  COPY_SUCCESS: 'Copied to clipboard.',
} as const;

// === Local Storage Keys ===
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'aditas_auth_token',
  REFRESH_TOKEN: 'aditas_refresh_token',
  USER_DATA: 'aditas_user_data',
  THEME: 'aditas_theme',
  LANGUAGE: 'aditas_language',
  PREFERENCES: 'aditas_preferences',
  RECENT_SEARCHES: 'aditas_recent_searches',
  DRAFT_POSTS: 'aditas_draft_posts',
} as const;

// === Date Formats ===
export const DATE_FORMATS = {
  SHORT: 'MMM dd, yyyy',
  LONG: 'MMMM dd, yyyy',
  WITH_TIME: 'MMM dd, yyyy HH:mm',
  TIME_ONLY: 'HH:mm',
  ISO: "yyyy-MM-dd'T'HH:mm:ss.SSSxxx",
  API: 'yyyy-MM-dd',
} as const;

// === Regex Patterns ===
export const REGEX_PATTERNS = {
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  PASSWORD: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
  USERNAME: /^[a-zA-Z0-9_]+$/,
  SLUG: /^[a-z0-9-]+$/,
  URL: /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/,
  PHONE: /^\+?[\d\s\-()]+$/,
} as const;

// === File Constants ===
export const FILE_CONSTANTS = {
  MAX_SIZE: {
    IMAGE: 5 * 1024 * 1024, // 5MB
    VIDEO: 50 * 1024 * 1024, // 50MB
    DOCUMENT: 10 * 1024 * 1024, // 10MB
  },
  ALLOWED_TYPES: {
    IMAGE: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
    VIDEO: ['video/mp4', 'video/webm', 'video/ogg'],
    DOCUMENT: ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
  },
} as const;

// === Pagination Constants ===
export const PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_LIMIT: 10,
  MAX_LIMIT: 100,
  PAGE_SIZE_OPTIONS: [10, 25, 50, 100],
} as const;

// === Animation Durations ===
export const ANIMATION_DURATION = {
  FAST: 150,
  NORMAL: 300,
  SLOW: 500,
  VERY_SLOW: 1000,
} as const;

// === Breakpoints ===
export const BREAKPOINTS = {
  XS: 0,
  SM: 640,
  MD: 768,
  LG: 1024,
  XL: 1280,
  '2XL': 1536,
} as const;

// === Z-Index Layers ===
export const Z_INDEX = {
  DROPDOWN: 1000,
  STICKY: 1020,
  FIXED: 1030,
  MODAL_BACKDROP: 1040,
  MODAL: 1050,
  POPOVER: 1060,
  TOOLTIP: 1070,
  TOAST: 1080,
} as const;

// === Feature Flags ===
export const FEATURE_FLAGS = {
  ENABLE_ANALYTICS: true,
  ENABLE_ERROR_REPORTING: true,
  ENABLE_PWA: false,
  ENABLE_DARK_MODE: true,
  ENABLE_I18N: false,
  ENABLE_BLOG_COMMENTS: true,
  ENABLE_PROJECT_FILTERING: true,
} as const;

// === Contact Information ===
// Using environment configuration (no hardcoded values per Rule 4)
// ENV is imported at the top of the file
export const CONTACT_INFO = {
  EMAIL: ENV.contact.email,
  SUPPORT_EMAIL: ENV.contact.supportEmail,
  PHONE: ENV.contact.phone,
  ADDRESS: ENV.contact.address,
} as const;

// === Social Media URLs ===
// Using environment configuration (no hardcoded values per Rule 4)
// ENV is imported at the top of the file
export const SOCIAL_MEDIA = {
  TWITTER: ENV.social.twitter,
  LINKEDIN: ENV.social.linkedin,
  GITHUB: ENV.social.github,
  FACEBOOK: ENV.social.facebook,
  INSTAGRAM: ENV.social.instagram,
} as const;
